import { RiskyDisclosureParagraph } from '@/components/common/DisclosureParagraph';
import { InfoButtonWithModal } from '@/components/common/InfoModal';
import { RadioInputLabel } from '@/components/common/RadioInput';
import { RadioInputGroupProps } from '@/components/common/RadioInputGroup';
import { Paragraph } from '@/components/common/Typography';
import IdvIntro from '@/components/idv/IdvIntro';
import { useTrackPageViewed } from '@/services/analytics/useTracking';
import { Box } from '@chakra-ui/react';
import { INVESTMENT_PRIORITY_OPTIONS } from '@common/inputs';
import { InvestmentProfile } from '@common/types';
import {
    BalanceRiskChart,
    MaximizeReturnsChart,
    MinimizeLossesChart,
} from '../chart-images';
import { IdvForm } from '../IdvForm';

type T = { investmentPriority: InvestmentProfile['investment_priority'] };

const options: RadioInputGroupProps['options'] =
    INVESTMENT_PRIORITY_OPTIONS.map(({ label, description, value }) => ({
        label: (
            <RadioInputLabel key={value} label={label}>
                <Paragraph size="sm" color="gray" position="relative">
                    {description}
                    <Box
                        as="span"
                        bg="white"
                        position="absolute"
                        left={-24}
                        right="-12px"
                        top={-12}
                        bottom={-5}
                        zIndex={-1}
                        _after={{
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: '100%',
                            bottom: 0,
                            w: 24,
                            bg: 'linear-gradient(90deg, white 0%, transparent 100%)',
                        }}
                    />
                </Paragraph>
                {
                    {
                        AVOID_LOSSES: <MinimizeLossesChart />,
                        BALANCE_RISK: <BalanceRiskChart />,
                        MAXIMIZE_RETURNS: <MaximizeReturnsChart />,
                    }[value]
                }
            </RadioInputLabel>
        ),
        value,
    }));

export const InvestmentPriorityView = () => {
    useTrackPageViewed('IDV > InvestmentPriorityView');

    return (
        <IdvForm<T>
            initValues={(userProfile) => ({
                investmentPriority:
                    userProfile?.investmentProfile?.investment_priority ||
                    undefined,
            })}
            toMutationInput={({ investmentPriority }) => ({
                investmentProfile: { investment_priority: investmentPriority },
            })}
            backNextNavProps={{
                onClickEventParams: {
                    location: 'IDV > InvestmentPriorityView',
                },
            }}
        >
            <IdvIntro
                title={
                    <>
                        My primary goal when investing...
                        <InfoButtonWithModal
                            title="My primary goal when investing..."
                            buttonProps={{
                                position: 'relative',
                                top: '-1px',
                                onClickEventParams: {
                                    location: 'IDV > InvestmentPriorityView',
                                },
                            }}
                            color="gray"
                        >
                            <Paragraph>
                                We ask this to understand how you like to
                                invest.
                            </Paragraph>
                            <Paragraph>
                                Once we understand your profile as an investor,
                                we’ll combine that with the details of your
                                goals to create personalized portfolio
                                recommendations.
                            </Paragraph>
                            <Paragraph>
                                When you choose to minimize losses, we’ll create
                                a lower risk portfolio that is less likely to go
                                down. That means you’ll hold more bonds and
                                treasuries, which tend to be more stable.
                            </Paragraph>
                            <Paragraph>
                                When you choose to maximize returns, we’ll
                                create a higher risk portfolio that may go up
                                (or down) more. That means you’ll hold more US
                                and international stocks.
                            </Paragraph>
                        </InfoButtonWithModal>
                    </>
                }
                desc="We ask this to provide recommendations for how to save and invest."
            />
            <IdvForm.Input<T>
                name="investmentPriority"
                label=""
                type="radio"
                options={options}
                radioProps={{ mb: 1 }}
                isRequired
                autoFocus
            />
            <RiskyDisclosureParagraph mt={8} />
        </IdvForm>
    );
};
